import { CustomTableColorColumn } from "components/dynamic-table/color-table";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Convenio } from "../atendimento-form/types";
import { Toast } from "components/toast";
import apiDigitacao from "api/api-digitacao";
import { dropdownConvenioAtendimento } from "../atendimento-form/fields-data";
import { currencyMask } from "utils/string-formats";
import { IconButtonWithLoading } from "components/mailing-components/mailing-table/icon-button-with-loading";
import { tiposOperacaoDigitados } from "../pagina-atendimento/pagined-form/parts/digitacao/digitados-part";
import { DocumentIcon } from "components/vectors/document-icon";
import { HamburgerIcon } from "@chakra-ui/icons";
import { UseRouterReturn } from "router-manager/parts";

export interface PropostasFilterProps {
  page: number;
  size: number;
  convenio: Convenio;
  ade?: string;
  operacao?: string;
  dataInicio?: string;
  dataFim?: string;
  userIdDigitador?: null;
  banco?: string;
}

export type UseTabelaPropostaFilterReturn = ReturnType<
  typeof useTabelaPropostaFilters
>;

export function useTabelaPropostaFilters({
  customer_id,
  setRows
}: {
  customer_id: number;
  setRows: Dispatch<SetStateAction<TablePropostaRowProps[]>>;
}) {
  const [errors, setErrors] = useState<{ [k: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  let [filter, setFilter] = useState<PropostasFilterProps>({
    convenio: "INSS",
    page: 0,
    size: 10
  });

  const onChange = (
    value: any,
    key: keyof PropostasFilterProps,
    params: { update: boolean } = { update: true }
  ) => {
    filter = { ...filter, [key]: value };

    if (value === undefined) delete filter[key];

    setFilter(filter);

    if (params.update) {
      onSearch(filter);
    }
  };

  useEffect(() => {
    if (filter.dataInicio && !filter.dataFim) {
      setErrors((errors) => ({ ...errors, dataFim: "Preencha o campo" }));
    } else {
      setErrors((errors) => {
        delete errors["dataFim"];
        return { ...errors };
      });
    }
    if (filter.dataFim && !filter.dataInicio) {
      setErrors((errors) => ({ ...errors, dataInicio: "Preencha o campo" }));
    } else {
      setErrors((errors) => {
        delete errors["dataInicio"];
        return { ...errors };
      });
    }
  }, [filter]);

  const onSearch = async (currentFilter = filter) => {
    setIsLoading(true);
    try {
      const { data } = await apiDigitacao.post(
        `/api/propostas-digitacao`,
        currentFilter,
        { params: { customer_id } }
      );
      setRows(data);
    } catch (e) {
      Toast({ title: "Erro ao obter propostas", status: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    onSearch();
  }, [customer_id]);

  return { filter, onChange, isLoading, onSearch, errors };
}

export function tabelaPropostaColumns({
  setRows,
  router
}: {
  setRows: Dispatch<SetStateAction<TablePropostaRowProps[]>>;
  router: UseRouterReturn;
}): CustomTableColorColumn<TablePropostaRowProps>[] {
  const onChangeRow = (newRow: TablePropostaRowProps) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.ade === newRow.ade) return newRow;
        return row;
      })
    );
  };

  const acompanharProposta = async (row: TablePropostaRowProps) => {
    const url =
      row.convenio === "INSS"
        ? `/api/digitacoes/inss/consultar-andamento-ade?digitacao_id=${row.digitacaoId}`
        : row.convenio === "ANTECIPACAO_FGTS"
          ? `/api/digitacoes/fgts/consultar-andamento-ade?digitacao_id=${row.digitacaoId}`
          : "";
    try {
      const { data } = await apiDigitacao.get(url);
      const lastIndex = data.length - 1;
      row.statusAde = data[lastIndex].status;
      onChangeRow(row);
    } catch (e) {
      Toast({
        title: "Erro ao consultar o acompanhamento da proposta",
        description: "Tente novamente",
        status: "error"
      });
    }
  };

  return [
    {
      name: "ADE",
      render: (row) => {
        return row.ade;
      }
    },
    {
      name: "Cpf cliente",
      render: (row) => {
        return row.cpf;
      }
    },
    {
      name: "Nome cliente",
      render: (row) => {
        return row.nome;
      }
    },
    {
      name: "Vendedor",
      render: (row) => {
        return row.username;
      }
    },
    {
      name: "Data digitação",
      render: (row) => {
        if (row.dataDigitacao)
          return new Date(row.dataDigitacao).toLocaleDateString();
      }
    },
    {
      name: "Convênio",
      render: (row) => {
        return (
          dropdownConvenioAtendimento.find(
            (curr) => curr.value === row.convenio
          )?.name ?? row.convenio
        );
      }
    },
    {
      name: "Banco",
      render: (row) => {
        return row.banco;
      }
    },
    {
      name: "Operação",
      render: (row) => {
        return (
          tiposOperacaoDigitados.find(({ value }) => value === row.operacao)
            ?.name ||
          row.operacao ||
          ""
        );
      }
    },
    {
      name: "Valor (líquido)",
      render: (row) => {
        return currencyMask(`${row.valorLiquido ?? ""}`);
      }
    },
    {
      name: "Status banco",
      render: (row) => {
        return row.statusAde;
      }
    },
    {
      name: "Ações",
      render: (row) => {
        return (
          <>
            <IconButtonWithLoading
              aria-label=""
              tooltipMessage="Acompanhar"
              onClick={() => acompanharProposta(row)}
              mr="8px"
              icon={<DocumentIcon width="20px" height="20px" />}
            ></IconButtonWithLoading>
            <IconButtonWithLoading
              aria-label=""
              tooltipMessage="Ir para atendimento"
              onClick={async () => {
                router.push(
                  `/atendimento-cliente-page?id=${row.atendimentoId}&init=false`
                );
              }}
              icon={<HamburgerIcon w="18px" h="18px" />}
            ></IconButtonWithLoading>
          </>
        );
      }
    }
  ];
}

export interface TablePropostaRowProps {
  digitacaoId: number;
  ade: string;
  cpf: string;
  nome: string;
  username: string;
  dataDigitacao: string;
  convenio: string;
  banco: string;
  operacao: string;
  valorLiquido: number;
  statusAde: string;
  atendimentoId: number;
}
